import React, { FunctionComponent, ReactElement, useEffect, useMemo, useState } from "react";
import {
    AllCommunityModule,
    ClientSideRowModelModule,
    ColDef,
    DomLayoutType,
    GridOptions,
    ModuleRegistry,
    RowSelectionModule,
    Theme,
    ValidationModule
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import "./AgGridTable.css"
import { themeQuartz } from 'ag-grid-community';


export type AgGridTableProps = {
    domLayout: DomLayoutType;
    customStyles?: Theme | "legacy" | undefined;
    columns: ColDef[];
    data: any[] | null;
    pagination: boolean;
    paginationPerPage: number;
    rowSelection: "single" | "multiple";
    isSelectableRows?: boolean;
    handleSelectedRows: (selectedRows: any[]) => void;

    noDataComponentText: string;

    gridOptions: GridOptions;
};

ModuleRegistry.registerModules([
    AllCommunityModule,
    RowSelectionModule,
    ClientSideRowModelModule,
    ValidationModule
]);

export const AgGridTable: FunctionComponent<AgGridTableProps> = ({
    domLayout,
    customStyles,
    columns,
    data,
    pagination,
    paginationPerPage,
    rowSelection,
    handleSelectedRows,
    noDataComponentText,
    gridOptions
}): ReactElement => {

    const memoizedColumns = useMemo(() => {
        return columns.map((col) => ({
            ...col,
            resizable: true,
            flex: 1, // Makes columns adjust dynamically
        }));
    }, [columns]);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);




    // to use myTheme in an application, pass it to the theme grid option
    const myTheme = themeQuartz
        .withParams({
            browserColorScheme: "light",
            fontSize: 13,
            cellTextColor: "#353535",
            headerFontSize: 12,
            headerTextColor: "#B5B5C3",
            spacing: 8,
            wrapperBorderRadius: 8,
            borderColor: "#8F999D26",
            wrapperBorder: false,
            headerVerticalPaddingScale: 0.9,
        });

    return (
        <>
            {isMobile ? (
                // 📱 Mobile View - Form Style
                <div className="mobile-form-container">
                    {data && data.length > 0 ? (
                        data.map((row, index) => (
                            <div key={index} className="mobile-form-card">
                                {columns.map((col) => (
                                    <div key={col.field} className="mobile-form-field">
                                        <strong>{col.headerName}:</strong>
                                    </div>
                                ))}
                            </div>
                        ))
                    ) : (
                        <p className="no-data-text">{noDataComponentText}</p>
                    )}
                </div>
            ) : (
                // 🖥️ Desktop View - Table
                <div
                    className={`ag-theme-quartz ${customStyles ? `ag-theme-${customStyles}` : ""}`}
                    style={{ height: "100%", width: "100%", overflowX: "auto" }} // Enables horizontal scrolling
                >
                    <AgGridReact
                        domLayout={domLayout}
                        columnDefs={memoizedColumns}
                        rowData={data || []}
                        pagination={pagination}
                        paginationPageSize={paginationPerPage}
                        rowSelection={rowSelection}
                        onSelectionChanged={(event) => handleSelectedRows(event.api.getSelectedRows())}
                        overlayNoRowsTemplate={noDataComponentText}
                        onCellKeyDown={gridOptions?.onCellKeyDown}
                        gridOptions={{
                            ...gridOptions,
                            theme: myTheme, // 👈 inject your custom theme here
                            suppressHorizontalScroll: false,
                            suppressMenuHide: true,
                            suppressClickEdit: true,
                            sideBar: {
                                toolPanels: [
                                    {
                                        id: 'filters',
                                        labelDefault: 'Search',
                                        labelKey: 'search',
                                        iconKey: 'filter',
                                        toolPanel: 'agFiltersToolPanel',
                                    },
                                ],
                                defaultToolPanel: 'filters',
                            },
                        }}
                    />
                </div>
            )}
        </>
    );
};
